import React, { useState, useEffect } from "react";
import {
  Modal,
  Button,
  Tree,
  Message,
  useToaster,
} from "rsuite";
import { handleToggle } from "../QueryEditorPageUtil/QueryEditorPageUtil.js";
import { queryPostRequest } from "../../api";
import "./QueryTree.css";
import TrashIcon from "@rsuite/icons/Trash";
import HistoryIcon from "@rsuite/icons/History";
import SaveIcon from "@rsuite/icons/Save";

const QueryTree = ({ userId, onSavedQueryClick, setQueryHistoryOpen, setPanelOpen, setIsRightToggled, queryHistory, setQueryHistory, setQueryHistoryLoading, setActiveIconRight, refreshSavedQueriesRef, datasources}) => {
  useEffect(() => {
    if (refreshSavedQueriesRef) {
      refreshSavedQueriesRef.current = refreshSavedQueries;
    }
  }, [refreshSavedQueriesRef]);

  const [treeData, setTreeData] = useState([
    {
      value: "query_history",
      label: (
        <>
          <HistoryIcon className="tree-icon" />
          Query history
        </>
      ),
      children: null,
    },
    {
      value: "saved_queries",
      label: (
        <>
          <SaveIcon className="tree-icon" />
          Saved queries
        </>
      ),
      children: [],
    },
  ]);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteTitle, setDeleteTitle] = useState(null);
  const [panelWidth, setPanelWidth] = useState(960);
  const toaster = useToaster();


  const handleExpand = async (expandedNodes) => {
    const expandedNode = expandedNodes[expandedNodes.length - 1];
    let newData = [...treeData];

    if (expandedNode === "saved_queries") {
      try {
        const data = await queryPostRequest("fetch_saved_queries", {
          user_id: userId,
        });
        newData = newData.map((node) =>
          node.value === "saved_queries"
            ? {
                ...node,
                children: Object.entries(data).map(([title, queryData]) => {
                  let query = typeof queryData === "string" ? queryData : queryData.query;
                  let datasource = queryData.datasource;
  
                  // If datasource is missing, find the first matching datasource in the query
                  if (!datasource) {
                    datasource =
                      (query && datasources.find((ds) => query.includes(ds.value))?.value) || "";
                  }
    
                  return {
                  value: title,
                  label: (
                    <span className="saved-query-title">
                      {title.length > 20 ? `${title.slice(0, 20)}` : title}
                      <TrashIcon
                        className="query-delete-icon"
                        onClick={(e) => {
                          e.stopPropagation();
                          setDeleteTitle(title);
                          setShowDeleteModal(true);
                        }}
                        color="red"
                      />
                    </span>
                  ),
                  isLeaf: true,
                  query: query,
                  datasource: datasource,
                };
              }),
            }
          : node
        );
      } catch (error) {
        console.error("Failed to fetch saved queries", error);
      }
    }
    setTreeData(newData);
  };

  const refreshSavedQueries = async () => {
  try {
    const data = await queryPostRequest("fetch_saved_queries", {
      user_id: userId,
    });
    setTreeData((prevTreeData) =>
      prevTreeData.map((node) =>
        node.value === "saved_queries"
          ? {
              ...node,
              children: Object.entries(data).map(([title, queryData]) => {
                let query = typeof queryData === "string" ? queryData : queryData.query;
                let datasource = queryData.datasource;

                // If datasource is missing, find the first matching datasource in the query
                if (!datasource) {
                  datasource =
                    (query && datasources.find((ds) => query.includes(ds.value))?.value) || "";
                }
  
                return {
                value: title,
                label: (
                  <span className="saved-query-title">
                    {title.length > 20 ? `${title.slice(0, 20)}` : title}
                    <TrashIcon
                      className="query-delete-icon"
                      onClick={(e) => {
                        e.stopPropagation();
                        setDeleteTitle(title);
                        setShowDeleteModal(true);
                      }}
                      color="red"
                    />
                  </span>
                ),
                isLeaf: true,
                query: query,
                datasource: datasource,
             };
            }),
          }
        : node
      )
    );
  }  catch (error) {
    console.error("Failed to fetch saved queries", error);
  }
};

  const handleClick = async (node) => {
    if (node.value === "query_history") {
      setPanelOpen(false);
      setActiveIconRight("right");
      handleToggle(true, setIsRightToggled);
      setPanelWidth(960);
      setQueryHistoryOpen(true);
      setQueryHistoryLoading(true);
      try {
        const data = await queryPostRequest("get_query_history", {
          user_id: userId,
        });
        setQueryHistory(data.query_history || []);
      } catch (error) {
        console.error("Failed to fetch query history", error);
        setQueryHistory([]);
      } finally {
        setQueryHistoryLoading(false);
      }
    } else if (node.query) {
      if (onSavedQueryClick) {
        onSavedQueryClick(node.value, node.query, node.datasource);
      }
    }
  };

  const handleDeleteQuery = async () => {
    const requestBody = {
      user_id: userId,
      title: deleteTitle,
    };

    try {
      await queryPostRequest("delete_saved_query", requestBody);
      setTreeData((prevTreeData) =>
        prevTreeData.map((node) => {
          if (node.value === "saved_queries" && node.children) {
            return {
              ...node,
              children: node.children.filter(
                (child) => child.value !== deleteTitle
              ),
            };
          }
          return node;
        })
      );
      setShowDeleteModal(false);
      toaster.push(
        <Message type="success" showIcon closable>
          Deleted <strong>{deleteTitle}</strong>
        </Message>,
        { placement: "topCenter", duration: 3000 }
      );
    } catch (error) {
      console.error("Failed to delete saved query", error);
      setShowDeleteModal(false);
      toaster.push(
        <Message type="error" showIcon closable>
          Error! Could not delete <strong>{deleteTitle}</strong>
        </Message>,
        { placement: "topCenter", duration: 3000 }
      );
    }
  };

  return (
    <div className="query-tree">
      <Tree
        data={treeData}
        onExpand={(expandedNodes) => {
          handleExpand(expandedNodes);
        }}
        onSelect={(activeNode) => handleClick(activeNode)}
        defaultExpandAll={false}
        showIndentLine
      />
      <Modal open={showDeleteModal} onClose={() => setShowDeleteModal(false)}>
        <Modal.Header>
          <Modal.Title>Delete Query</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          Are you sure you want to delete <strong>{deleteTitle}</strong>?
        </Modal.Body>
        <Modal.Footer>
          <Button onClick={() => setShowDeleteModal(false)} appearance="subtle">
            Cancel
          </Button>
          <Button onClick={handleDeleteQuery} appearance="primary" color="red">
            Delete
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default QueryTree;